import {
  toPurchaseOrder,
  toPurchaseOrderList,
  type PurchaseOrder,
  type PurchaseOrderList,
  type PurchaseOrderStatus,
} from "@xop-any-time/shared";

import { ApiError } from "./api.js";
import { downloadToDisk, fetchBytes } from "./companyDocumentsApi.js";
import { env } from "./env.js";

/**
 * Purchase orders — this reseller's orders TO XOP Networks (2026-09-17).
 *
 * Every call rides the BFF's `/api/crm/v1/*` passthrough to crm-api's
 * `/api/v1/purchase_orders*`, as the signed-in reseller. Nothing here decides
 * who may do what: crm-api gates reads on `invoices:view` and writes on
 * `invoices:edit`, scopes rows to the company's own subtree, and says per row
 * whether THIS person may still edit it (`editable`) or withdraw it
 * (`cancellable`).
 *
 * crm-api's snake_case stops at this file: every answer goes through the
 * shared `toPurchaseOrder` mapper before a page sees it.
 */

const BASE = "/api/crm/v1/purchase_orders";

export const purchaseOrderKeys = {
  all: ["purchase-orders"] as const,
  lists: ["purchase-orders", "list"] as const,
  list: (params: PurchaseOrderListParams) => ["purchase-orders", "list", params] as const,
  detail: (id: number) => ["purchase-orders", "detail", id] as const,
  pdf: (id: number, updatedAt: string | null) => ["purchase-orders", "pdf", id, updatedAt] as const,
  document: (id: number, updatedAt: string | null) => ["purchase-orders", "document", id, updatedAt] as const,
};

export interface PurchaseOrderListParams {
  page: number;
  status: PurchaseOrderStatus | "all";
  search?: string;
}

export interface NewPurchaseOrderInput {
  document: File;
  poNumber?: string;
  /** `YYYY-MM-DD`. */
  orderDate?: string;
  customerId?: number;
  totalCents?: number;
  note?: string;
}

export interface PurchaseOrderEdit {
  poNumber: string | null;
  note: string | null;
}

/**
 * The passthrough answers a refusal as `{ error: "<crm-api's sentence>" }` —
 * the joined validation messages for a 422 — while the JSON helper in `api.ts`
 * reads `message`. So these calls read both, and a 422 reaches the form as
 * the sentence crm-api wrote ("That customer is not one of yours.").
 */
async function toApiError(res: Response): Promise<ApiError> {
  let body: { message?: string; error?: string; code?: string } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    /* non-JSON — keep the status line */
  }
  return new ApiError(
    body.message ?? body.error ?? `Request failed with status ${res.status}`,
    res.status,
    body.code,
  );
}

async function send(path: string, init: RequestInit = {}): Promise<unknown> {
  const res = await fetch(`${env.apiUrl}${path}`, { ...init, credentials: "include" });
  if (!res.ok) throw await toApiError(res);
  if (res.status === 204) return {};
  return res.json();
}

function jsonInit(method: string, body: unknown): RequestInit {
  return { method, headers: { "content-type": "application/json" }, body: JSON.stringify(body) };
}

/** The one row out of `{ purchase_order: … }`; a 2xx without one is a body the
 * page cannot render, and it says so rather than rendering a blank document. */
function one(body: unknown): PurchaseOrder {
  const po = toPurchaseOrder((body as { purchase_order?: unknown } | null)?.purchase_order);
  if (po === null) throw new ApiError("The CRM returned a purchase order we could not read.", 502);
  return po;
}

export function purchaseOrderListQuery(params: PurchaseOrderListParams): string {
  const q = new URLSearchParams();
  q.set("page", String(params.page));
  q.set("per_page", "25");
  if (params.status !== "all") q.set("status", params.status);
  if (params.search && params.search.trim() !== "") q.set("search", params.search.trim());
  return q.toString();
}

export async function listPurchaseOrders(params: PurchaseOrderListParams): Promise<PurchaseOrderList> {
  return toPurchaseOrderList(await send(`${BASE}?${purchaseOrderListQuery(params)}`));
}

export async function getPurchaseOrder(id: number): Promise<PurchaseOrder> {
  return one(await send(`${BASE}/${id}`));
}

/**
 * The multipart envelope crm-api's `POST /purchase_orders` reads. Exported so
 * the field names — the cross-repo contract — are unit-tested directly.
 *
 * No `reseller_id`: crm-api forces it to the actor's own company, and sending
 * one would only suggest this page gets a say.
 */
export function newPurchaseOrderForm(input: NewPurchaseOrderInput): FormData {
  const form = new FormData();
  form.append("document", input.document);
  const field = (name: string, value: string | number | undefined): void => {
    if (value === undefined) return;
    const text = String(value).trim();
    if (text !== "") form.append(`purchase_order[${name}]`, text);
  };
  field("po_number", input.poNumber);
  field("order_date", input.orderDate);
  field("customer_id", input.customerId);
  field("total_cents", input.totalCents);
  field("note", input.note);
  return form;
}

export async function createPurchaseOrder(input: NewPurchaseOrderInput): Promise<PurchaseOrder> {
  return one(await send(BASE, { method: "POST", body: newPurchaseOrderForm(input) }));
}

/** A reseller may change only its PO number and the note, and only while the
 * order is still `received` — crm-api ignores anything else. */
export async function updatePurchaseOrder(id: number, edit: PurchaseOrderEdit): Promise<PurchaseOrder> {
  return one(
    await send(
      `${BASE}/${id}`,
      jsonInit("PATCH", { purchase_order: { po_number: edit.poNumber, note: edit.note } }),
    ),
  );
}

/**
 * Withdraw this order (2026-09-17).
 *
 * The reseller's half of the CEO's ruling: staff DELETE a purchase order, a
 * reseller CANCELS one. There is no delete on this side and there must never
 * be — a reseller's order is a record XOP has already been told about, so it
 * is withdrawn in place, and crm-api keeps the row, the stamp and the audit.
 *
 * Its own endpoint rather than `PATCH … { status }`: a reseller may not set
 * the status (crm-api ignores the field from this side), and an endpoint that
 * means one thing can be allowlisted for reseller actors without widening the
 * edit. A refusal — too late, or XOP's own uploaded record — comes back as a
 * 409 with crm-api's sentence, which the page shows verbatim.
 */
export async function cancelPurchaseOrder(id: number): Promise<PurchaseOrder> {
  return one(await send(`${BASE}/${id}/cancel`, { method: "POST" }));
}

/** Attach or replace the signed PO document. Field name `document`. */
export async function uploadPurchaseOrderDocument(id: number, file: File): Promise<PurchaseOrder> {
  const form = new FormData();
  form.append("document", file);
  return one(await send(`${BASE}/${id}/document`, { method: "POST", body: form }));
}

/**
 * Make the purchase order for a quote this reseller has just accepted.
 *
 * Idempotent upstream: 201 with the new PO, or 200 with the one that already
 * exists — both are "here is your purchase order", so both answer the same.
 */
export async function createPurchaseOrderFromQuote(
  quoteId: number,
  input: { poNumber?: string; note?: string } = {},
): Promise<PurchaseOrder> {
  const po_number = input.poNumber?.trim();
  const note = input.note?.trim();
  const fields = {
    ...(po_number ? { po_number } : {}),
    ...(note ? { note } : {}),
  };
  return one(
    await send(
      `/api/crm/v1/reseller_quotes/${quoteId}/purchase_order`,
      jsonInit("POST", Object.keys(fields).length > 0 ? { purchase_order: fields } : {}),
    ),
  );
}

export function purchaseOrderPdfUrl(id: number): string {
  return `${env.apiUrl}${BASE}/${id}/pdf`;
}

export function purchaseOrderDocumentUrl(id: number, disposition: "inline" | "attachment" = "inline"): string {
  return `${env.apiUrl}${BASE}/${id}/document?disposition=${disposition}`;
}

/** Bytes through this origin, for the inline pdf.js reader. */
export const fetchPurchaseOrderPdf = (id: number) => fetchBytes(purchaseOrderPdfUrl(id));
export const fetchPurchaseOrderDocument = (id: number) => fetchBytes(purchaseOrderDocumentUrl(id, "inline"));

export function purchaseOrderPdfFilename(po: Pick<PurchaseOrder, "id" | "number">): string {
  return `${po.number ?? `purchase-order-${po.id}`}.pdf`;
}

export function downloadPurchaseOrderPdf(po: Pick<PurchaseOrder, "id" | "number">): Promise<void> {
  return downloadToDisk(purchaseOrderPdfUrl(po.id), purchaseOrderPdfFilename(po));
}

export function downloadPurchaseOrderDocument(
  po: Pick<PurchaseOrder, "id" | "number" | "document">,
): Promise<void> {
  return downloadToDisk(
    purchaseOrderDocumentUrl(po.id, "attachment"),
    po.document?.filename ?? `${po.number ?? `purchase-order-${po.id}`}-document`,
  );
}

/** Is the attached document something pdf.js can show? */
export function documentIsPdf(po: Pick<PurchaseOrder, "document">): boolean {
  const d = po.document;
  if (!d) return false;
  return d.contentType === "application/pdf" || /\.pdf$/i.test(d.filename);
}

/** Today in the viewer's own calendar, `YYYY-MM-DD` — the order-date default.
 * NOT `toISOString().slice(0, 10)`, which is the UTC date — the wrong day for
 * anyone away from UTC near midnight. */
export function todayLocal(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
